import React, { useState } from "react";
import { useSearchParams } from "react-router-dom";
import CatalogPageShell from "../components/Products/CatalogPageShell.jsx";
import DimensionGrid from "../components/Products/DimensionGrid.jsx";
import TabNavigation from "../components/TabNavigation.jsx";

const money = (key) => (row) => {
    const value = row?.[key];
    return typeof value === "number"
        ? value.toLocaleString("en-US", { style: "currency", currency: "USD" })
        : "";
};

const PACKING = {
    title: "packing configurations",
    singular: "Configuration",
    endpoint: "broylesllc/shipping-classes",
    maxWidth: "sm",
    fields: [
        { key: "name", label: "Name", required: true },
        {
            key: "build",
            label: "Build",
            type: "links",
            optionLabel: "internalName",
            optionsEndpoint: "api/broylesllc/box-configs?limit=500",
            hint: "Configuration Cost records this packing class is built from",
        },
    ],
    readOnly: [
        {
            key: "costRollup",
            label: "Cost Rollup",
            value: money("costRollup"),
            source: "Sum of Cost per Unit on the Build links",
        },
    ],
};

const numberField = (key, label) => ({ key, label, type: "number" });

const COST = {
    title: "configuration costs",
    singular: "Configuration Cost",
    endpoint: "broylesllc/box-configs",
    maxWidth: "sm",
    fields: [
        { key: "internalName", label: "Internal Name", required: true },
        { key: "modelName", label: "Model Name" },
        numberField("quantityOnHand", "Quantity on Hand"),
        numberField("reorderPoint", "Reorder Point"),
        numberField("weightOz", "Weight (oz)"),
        numberField("costOnLastInvoice", "Cost on Last Invoice"),
        numberField("quantityOfItemOnLastInvoice", "Quantity (of Item) on Last Invoice"),
        numberField("quantityOfInvoiceOnLastInvoice", "Quantity (of Invoice) on Last Invoice"),
        numberField("shippingAndHandlingPlusTaxOnLastInvoice", "Shipping and Handling + Tax on Last Invoice"),
        {
            key: "singleUseMaterials",
            label: "Single Use Materials",
            type: "links",
            optionLabel: "name",
            optionsEndpoint: "api/broylesllc/packing-materials?limit=500",
            hint: "Packing materials whose Cost / Unit is summed for this configuration",
        },
        {
            key: "tapeOnly",
            label: "Tape Only",
            type: "links",
            optionLabel: "name",
            optionsEndpoint: "api/broylesllc/packing-materials?limit=500",
            hint: "Tape material whose Cost / Unit is the tape cost per inch",
        },
        {
            key: "packingConfigurations",
            label: "Box Packing Configurations",
            type: "links",
            optionLabel: "name",
            optionsEndpoint: "api/broylesllc/shipping-classes?limit=500",
            hint: "Same link as Build on a packing configuration",
        },
    ],
    readOnly: [
        { key: "costPerUnit", label: "Cost per Unit", value: money("costPerUnit") },
        {
            key: "boxLength",
            label: "Box Length",
            value: (row) => (row?.boxLength == null ? "" : String(row.boxLength)),
            source: "From Internal Name, or 18 when the name is Retail",
        },
        {
            key: "tapeNeededInches",
            label: "Tape Needed, Inches",
            value: (row) => (row?.tapeNeededInches == null ? "" : String(row.tapeNeededInches)),
        },
        { key: "tapeCostPerInch", label: "Tape Cost, per inch", value: money("tapeCostPerInch"), source: "Sum of Cost / Unit on the Tape Only links" },
        { key: "totalTapeCost", label: "Total Tape Cost", value: money("totalTapeCost") },
        {
            key: "costPerUnitRollupFromSingleUseMaterials",
            label: "Cost / Unit Rollup (from Single Use Materials)",
            value: money("costPerUnitRollupFromSingleUseMaterials"),
            source: "Sum of Cost / Unit on the Single Use Materials links",
        },
        { key: "totalConfigurationCost", label: "Total Configuration Cost", value: money("totalConfigurationCost") },
    ],
};

const MATERIALS = {
    title: "packing material costs",
    singular: "Packing Material",
    endpoint: "broylesllc/packing-materials",
    maxWidth: "sm",
    fields: [
        { key: "name", label: "Name", required: true },
        numberField("packCost", "Pack Cost"),
        numberField("unitsPerPack", "Units / Pack"),
        { key: "orderLink", label: "Order Link" },
        {
            key: "boxConfigurationCost",
            label: "Box Configuration Cost",
            type: "links",
            optionLabel: "internalName",
            optionsEndpoint: "api/broylesllc/box-configs?limit=500",
            hint: "Configuration costs that use this as a single-use material",
        },
        {
            key: "boxConfigurationCost2",
            label: "Box Configuration Cost 2",
            type: "links",
            optionLabel: "internalName",
            optionsEndpoint: "api/broylesllc/box-configs?limit=500",
            hint: "Configuration costs that use this as tape",
        },
    ],
    readOnly: [
        {
            key: "costPerUnit",
            label: "Cost / Unit",
            value: money("costPerUnit"),
            source: "Pack Cost divided by Units / Pack",
        },
    ],
};

const TABS = [
    { label: "Packing Configurations", value: 0 },
    { label: "Configuration Cost", value: 1 },
    { label: "Packing Material Costs", value: 2 },
];

export default function BoxPacking() {
    const [searchParams, setSearchParams] = useSearchParams();
    const [tab, setTab] = useState(() => {
        const initial = Number(searchParams.get("t"));
        return initial === 1 || initial === 2 ? initial : 0;
    });

    const changeTab = (_event, next) => {
        setTab(next);
        setSearchParams({ t: String(next) });
    };

    return (
        <CatalogPageShell
            title="Box Packing"
            subtitle="Packing configurations, configuration costs, and the packing materials those costs are built from."
        >
            <div className="app-page-stack">
                <div style={{ flexShrink: 0 }}>
                    <TabNavigation value={tab} onChange={changeTab} tabs={TABS} />
                </div>
                {tab === 2 ? (
                    <DimensionGrid key="materials" spec={MATERIALS} />
                ) : tab === 1 ? (
                    <DimensionGrid key="cost" spec={COST} />
                ) : (
                    <DimensionGrid key="packing" spec={PACKING} />
                )}
            </div>
        </CatalogPageShell>
    );
}
